import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subject, pipe, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { APP_ROUTES } from '../../app.constants';
import { REGEX_PHONE } from '../../constants';
import { FileUploadResponse } from '../../interfaces/models';
import { PhoneNumberPipe } from '../../pipes/number-phone';
import { ErrorService } from '../../providers/error/error.service';
import { FileService } from '../../providers/file/file.service';
import { HereService, HereGeocodeResult } from '../../providers/here/here.service';
import { LoadingService } from '../../providers/loading/loading.service';
import { NotificationService } from '../../providers/notification/notification.service';
import { RegisterService } from '../../providers/register/register.service';
import { environment } from '../../../environments/environment';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { DestroyableComponent } from '../../core/utils/destroyable.component';
import { AddressAutocompleteService } from '../../providers/address-autocomplete/address-autocomplete.service';

const RECAPTCHA_ERROR_MESSAGE = 'Security check failed. Please refresh and try again.';

const REQUIRED_FIELDS = ['name',
  'address',
  'city',
  'state',
  'zip',
  'dotNumber',
  'msNumber',
  'officePhone',
  'email',
  'contactPersonFirstName',
  'contactPersonLastName',
];

@Component({
  selector: 'app-register-carrier',
  templateUrl: './register-carrier.component.html',
  styleUrls: ['./register-carrier.component.scss'],
})
export class RegisterCarrierComponent extends DestroyableComponent implements OnInit {
  private static readonly AUTOCOMPLETE_FLOW = 'carrier_registration';

  @Input() invite = true;
  @ViewChild('mcCertificateDoc') mcCertificateDoc;
  @ViewChild('insuranceDoc') insuranceDoc;
  validatorAddress$ = new Subject<string>();
  companyId: number;
  errors = {
    name: false,
    address: false,
    city: false,
    state: false,
    zip: false,
    dotNumber: false,
    msNumber: false,
    officePhone: false,
    email: false,
    contactPersonFirstName: false,
    contactPersonLastName: false,
    terms: false,
    insurance: false,
    mcCertificate: false,
  };

  private readonly debounceTime = 1000;

  safeDebounce = pipe(
    takeUntil(this.onDestroy),
    debounceTime(this.debounceTime),
    distinctUntilChanged(),
  );

  searchLocation = pipe(
    this.safeDebounce,
    map((searchText: string) => this.addressAutocompleteService.normalizeQuery(searchText)),
    filter((searchText: string) => this.addressAutocompleteService.hasMinimumQueryLength(searchText)),
    switchMap((searchText: string) => this.hereService.getGeocode(searchText).pipe(
      catchError(() => of([])),
    )),
  );

  constructor(
    private fb: FormBuilder,
    private registerService: RegisterService,
    public route: ActivatedRoute,
    private loadingService: LoadingService,
    private router: Router,
    private fileService: FileService,
    private errorService: ErrorService,
    private hereService: HereService,
    private notificationService: NotificationService,
    private phoneNumberPipe: PhoneNumberPipe,
    private recaptchaV3Service: ReCaptchaV3Service,
    private addressAutocompleteService: AddressAutocompleteService,
  ) {
    super();
  }

  public data = this.fb.group({
    name: new FormControl('', [Validators.required]),
    address: new FormControl('', [Validators.required]),
    city: new FormControl('', [Validators.required]),
    state: new FormControl('', [Validators.required]),
    zip: new FormControl('', [Validators.required]),
    dotNumber: new FormControl('', [Validators.required]),
    msNumber: new FormControl('', [Validators.required]),
    officePhone: new FormControl('', [Validators.required, Validators.pattern(REGEX_PHONE)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    contactPersonFirstName: new FormControl('', [Validators.required]),
    contactPersonLastName: new FormControl('', [Validators.required]),
    contactPersonPhone: new FormControl('', [Validators.pattern(REGEX_PHONE)]),
    terms: new FormControl(false, [Validators.requiredTrue]),
  });

  addressLocationOptions$ = this.data
    .get('address').valueChanges
    .pipe(
      map(value => this.addressAutocompleteService.getQueryValue(value)),
      tap((query: string) => this.addressAutocompleteService.trackTyped(RegisterCarrierComponent.AUTOCOMPLETE_FLOW, query)),
      this.searchLocation,
      tap((results) => {
        const query = this.addressAutocompleteService.getQueryValue(this.data.get('address').value);
        this.addressAutocompleteService.trackSuggestionsShown(
          RegisterCarrierComponent.AUTOCOMPLETE_FLOW,
          query,
          results.length,
        );
      }),
    );

  public mcCertificate: FileUploadResponse;
  public insurance: FileUploadResponse;
  private hash: string;
  displayAddressOption = (value: unknown) => this.addressAutocompleteService.getDisplayValue(value);

  ngOnInit() {
    const hash = this.route.snapshot.paramMap.get('hash');

    if (hash && hash.length) {
      this.hash = hash;
      this.invite ?
        this.loadHash(hash) :
        this.validateHash(hash);
    }
    this.onChangePhone();
    this.validatorAddress$.pipe(
      debounceTime(600),
      distinctUntilChanged())
      .subscribe(() => {
        this.checkAddress();
      });
  }

  public addressValidator(addressObj: HereGeocodeResult) {
    const label = this.addressAutocompleteService.getDisplayValue(addressObj);
    this.data.patchValue(
      this.addressAutocompleteService.buildSelectionPatch(addressObj, {
        stateFormat: 'fullName',
        zipFieldName: 'zip',
      }),
    );
    this.addressAutocompleteService.trackSelected(RegisterCarrierComponent.AUTOCOMPLETE_FLOW, addressObj);
    this.errors['address'] = false;
    this.errors['city'] = false;
    this.errors['state'] = false;
    this.errors['zip'] = false;
    this.validatorAddress$.next(label);
  }

  public formValidator(key: string) {
    this.errors[key] = false;
  }

  private checkAddress() {
    const { address, city, state, zip } = this.data.value;
    if (address || city || state || zip) {
      this.hereService.validateAddress({
        state,
        address,
        city,
        zipCode: zip,
      })
        .subscribe(
          () => {
            this.setErrors(null);
          },
          () => {
            this.setErrors({ notEquivalent: true });
          });
    }
  }

  private setErrors(err: ValidationErrors | null) {
    const { address, city, state, zip } = this.data.controls;
    address.setErrors(err);
    city.setErrors(err);
    state.setErrors(err);
    zip.setErrors(err);
  }

  private onChangePhone() {
    this.data.valueChanges.subscribe((val) => {
      const { contactPersonPhone, officePhone } = val;
      if (contactPersonPhone && contactPersonPhone.length) {
        const formatPhone = this.phoneNumberPipe.transform(contactPersonPhone);
        this.data.get('contactPersonPhone').patchValue(formatPhone, { emitEvent: false });
      }
      if (officePhone && officePhone.length) {
        const formatPhone = this.phoneNumberPipe.transform(officePhone);
        this.data.get('officePhone').patchValue(formatPhone, { emitEvent: false });
      }
    });
  }

  private loadHash(hash: string) {
    this.loadingService.startLoading();
    this.registerService.validateToken({ hash })
      .subscribe(
        (res) => {
          const company = res.invite.company;
          Object.keys(company).forEach((key) => {
            if (this.data.controls[key] && company[key]) {
              this.data.controls[key].setValue(company[key]);
              this.data.controls[key].disable();
            }
          });
          this.loadingService.stopLoading();
        },
        (err) => {
          this.loadingService.stopLoading();
          this.errorService.navigate(err);
        });
  }

  private setUrl(url: string) {
    const matchesUrl = url.match(/\/([^\/?#]+)[^\/]*$/);
    if (matchesUrl.length > 1) {
      return matchesUrl[1];
    }
    return url;
  }

  private validateHash(hash: string) {
    this.loadingService.startLoading();
    this.registerService.getByHash(hash)
      .subscribe(
        (res) => {
          Object.keys(res).forEach((key) => {
            if (this.data.controls[key] && res[key]) {
              this.data.controls[key].setValue(res[key]);
            }
            this.data.controls.email.disable();
            this.data.controls.terms.setValue(true);
            this.insurance = { signedUrl: res.insuranceUrl, url: this.setUrl(res.insuranceUrl) };
            this.mcCertificate = { signedUrl: res.mcCertificateUrl, url: this.setUrl(res.mcCertificateUrl) };
          });
          this.companyId = res.id;
          this.loadingService.stopLoading();
        },
        (err) => {
          this.loadingService.stopLoading();
          this.errorService.navigate(err);
        });
  }

  private validateKeys(keys: string[]): boolean {
    return keys.some(key => this.data.get(key).disabled ? false : !this.data.get(key).valid);
  }

  public validateFields() {
    return this.validateKeys(REQUIRED_FIELDS);
  }

  private getValue(key) {
    return this.data.get(key).value;
  }

  private uploadFile(file: File): Promise<FileUploadResponse> {
    if (!file) {
      return;
    }
    if (file.size / 1024 / 1024 > 1) {
      this.notificationService.error('File is bigger than 1MB');
      return;
    }
    this.loadingService.startLoading();
    return new Promise((resolve, reject) => {
      this.fileService.upload(file)
        .subscribe(
          (res) => {
            this.loadingService.stopLoading();
            resolve(res);
          },
          (err) => {
            this.loadingService.stopLoading();
            reject(err);
          });
    });
  }

  verifyErrors() {
    REQUIRED_FIELDS.forEach((key) => {
      this.errors[key] = this.data.get(key).disabled ? false : !this.data.get(key).valid;
    });
    this.errors.terms = !this.data.get('terms').valid;
    this.errors.insurance = !this.insurance;
    this.errors.mcCertificate = !this.mcCertificate;
  }

  public checkRegister() {
    this.verifyErrors();
    const terms = this.getValue('terms');
    if (!terms || this.validateFields() || !this.insurance || !this.mcCertificate) {
      return;
    }
    this.addressAutocompleteService.trackSubmitted(RegisterCarrierComponent.AUTOCOMPLETE_FLOW);
    this.loadingService.startLoading();

    const data = {
      mcCertificateUrl: this.mcCertificate.url || this.mcCertificate.signedUrl,
      insuranceUrl: this.insurance.url || this.insurance.signedUrl,
      name: this.getValue('name'),
      address: this.getValue('address'),
      city: this.getValue('city'),
      state: this.getValue('state'),
      zip: this.getValue('zip'),
      dotNumber: parseInt(this.getValue('dotNumber'), 10),
      msNumber: parseInt(this.getValue('msNumber'), 10),
      officePhone: this.getValue('officePhone'),
      contactPersonFirstName: this.getValue('contactPersonFirstName'),
      contactPersonLastName: this.getValue('contactPersonLastName'),
      contactPersonPhone: this.getValue('contactPersonPhone'),
      email: this.getValue('email'),
      termsOfServiceAccepted: this.getValue('terms'),
    };
    if (this.invite) {
      this.register({
        ...data,
        hash: this.hash,
      });
    } else {
      this.changeCompany(data);
    }
  }

  private changeCompany(data) {
    this.registerService.updateCarrier({
      ...data,
      id: this.companyId,
      token: this.hash,
    })
      .pipe(finalize(() => this.loadingService.stopLoading()))
      .subscribe({
        next: () => {
          this.notificationService.success('Success edit!');
          this.router.navigateByUrl(`/${APP_ROUTES.waiting}/requested-changes`);
        },
        error: (err) => {
          this.notificationService.error(this.getSubmitErrorMessage(err, 'Failed to update carrier information.'));
        },
      });
  }

  public async register(data) {

    if (environment.recatchaEnabled) {
      if (!this.recaptchaV3Service || typeof this.recaptchaV3Service.execute !== 'function') {
        this.notificationService.error(RECAPTCHA_ERROR_MESSAGE);
        this.loadingService.stopLoading();
        return;
      }

      this.recaptchaV3Service.execute('register_common')
        .pipe(
          finalize(() => this.loadingService.stopLoading()),
          map((token) => {
            data.token = token;
            return data;
          }),
          catchError(() => {
            this.notificationService.error(RECAPTCHA_ERROR_MESSAGE);
            return EMPTY;
          }),
        )
        .subscribe((payload) => {
          this.loadingService.startLoading();
          return this.doRegister(payload);
        });
      return;
    }
    return this.doRegister(data);
  }

  private async doRegister(data) {
    if (this.hash) {
      delete data.email;
      this.registerService.carrier(data)
        .pipe(finalize(() => this.loadingService.stopLoading()))
        .subscribe({
          next: (res) => {
            this.finishRegister(res.hash);
          },
          error: (err) => {
            this.notificationService.error(this.getSubmitErrorMessage(err, 'Failed to submit carrier registration.'));
          },
        });
    } else {
      this.registerService.carrierNew(data)
        .pipe(finalize(() => this.loadingService.stopLoading()))
        .subscribe({
          next: (res) => {
            this.finishRegister(res.hash);
          },
          error: (err) => {
            this.notificationService.error(this.getSubmitErrorMessage(err, 'Failed to submit carrier registration.'));
          },
        });
    }
  }

  private getSubmitErrorMessage(err, fallback: string): string {
    const errorMessage = err && (err.error && err.error.message || err.message || err.error);
    return typeof errorMessage === 'string' && errorMessage.trim().length ? errorMessage : fallback;
  }

  private finishRegister(hash: string) {
    this.router.navigateByUrl(`/${APP_ROUTES.register_carrier_finish}/${hash}`);
  }

  public login() {
    this.router.navigateByUrl('/auth');
  }

  public async selectMcCertificate(file: File) {
    this.mcCertificateDoc.nativeElement.value = null;
    this.mcCertificate = await this.uploadFile(file);
    this.errors.mcCertificate = false;
  }

  public async selectInsurance(file: File) {
    this.insuranceDoc.nativeElement.value = null;
    this.insurance = await this.uploadFile(file);
    this.errors.insurance = false;
  }

  public openTermService() {
    const url = this.router.serializeUrl(this.router.createUrlTree([APP_ROUTES.terms]));
    window.open(url, '_blank');
  }
}
